import { ArrowLeft } from "lucide-react"

import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { signFromDate } from "../../lib/zodiac"
import type { StepComponentProps } from "./step-contract"

/**
 * Full-bleed Soulmate interstitial / branch-reassurance screen (Step6.2, the
 * Step8.1 emotion/logic branches, the Step10.1 "Your struggle is real" branches).
 * A soft top glow behind the back-only navbar + title, a centered hero image, and
 * a Continue pinned at the bottom. Copy + image come from the step config, so one
 * component serves every interstitial. No step counter (uncounted screens).
 */
export function SoulmateInterstitialStep({ step, onAdvance, onBack, answers }: StepComponentProps) {
  // Personalize the "{sign} signs" copy with the user's zodiac sign (from the DOB
  // step), so the reassurance isn't the design's hardcoded placeholder.
  const dob = typeof answers.dob === "string" ? answers.dob : undefined
  const sign = dob ? signFromDate(dob) : "your"
  const subtitle = step.subtitle?.replace("{sign}", sign)

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light">
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="space-y-2 px-4 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
          {subtitle && <p className="text-base text-purple-light/70">{subtitle}</p>}
        </div>

        {step.coverImage && (
          <div className="flex flex-1 items-center justify-center px-4 py-6">
            <img
              src={step.coverImage}
              alt=""
              className="max-h-[360px] w-full max-w-[382px] object-contain"
            />
          </div>
        )}

        <div className="mt-auto px-4 pb-safe pt-2">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
